import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router';
import { Tabs } from '@artosera/ui';
import { usePeut } from '../../auth/usePeut.js';
import { obtenirOuverture } from '../../lib/api.js';
import {
  ETATS_PARCOURS,
  cleOuverture,
  estClos,
  estRevisionPerimee,
  formaterJour,
  messageErreurOuverture,
} from '../../lib/ouvertures.js';
import type { DossierOuverture } from '../../types.js';
import pageStyles from '../../styles/page.module.css';
import { BadgeTest } from '../EspaceDeTest.js';
import { BadgeEtatOuverture } from './commun.js';
import { OffreContrat } from './onglets/OffreContrat.js';
import { EquipeDossier, JournalDossier, RepriseDossier } from './onglets/EquipeEtReprise.js';
import { RecetteBascule } from './onglets/recette/RecetteBascule.js';
import { Synthese } from './onglets/Synthese.js';
import styles from './Ouvertures.module.css';

const ONGLETS = ['synthese', 'offre', 'equipe', 'reprise', 'recette', 'journal'] as const;
type Onglet = (typeof ONGLETS)[number];

function estOnglet(valeur: string | undefined): valeur is Onglet {
  return (ONGLETS as readonly string[]).includes(valeur ?? '');
}

/**
 * Un geste d'écriture sur le dossier : il reçoit le dossier courant (donc sa
 * révision), rend le dossier à jour. Une révision périmée relit le dossier et
 * le dit ; toute autre erreur s'affiche en tête de page. Rend `true` si le
 * geste a abouti.
 */
export type Ecriture = (
  geste: (courant: DossierOuverture) => Promise<DossierOuverture>,
) => Promise<boolean>;

/** `/ouvertures/:id` : la fiche d'un dossier (ARTO-128). */
export function DossierDetail(): JSX.Element {
  const { t } = useTranslation('console');
  const navigate = useNavigate();
  const { search } = useLocation();
  const peut = usePeut();
  const queryClient = useQueryClient();
  const params = useParams<{ id: string; onglet?: string }>();
  const id = params.id ?? '';

  const [erreur, setErreur] = useState<string | null>(null);
  const [avertissement, setAvertissement] = useState<string | null>(null);
  const [galerieAResilier, setGalerieAResilier] = useState<string | null>(null);

  const requete = useQuery({
    queryKey: cleOuverture(id),
    queryFn: () => obtenirOuverture(id),
    enabled: id !== '',
  });

  if (!estOnglet(params.onglet)) {
    return <Navigate to={{ pathname: `/ouvertures/${id}/synthese`, search }} replace />;
  }
  if (requete.isLoading) return <p className={pageStyles.videEtat}>{t('commun.chargement')}</p>;
  if (requete.isError || !requete.data) {
    return (
      <section>
        <p className={pageStyles.videEtat}>{t('ouvertures.introuvable')}</p>
        <Link to="/ouvertures" className="lien-cible">
          {t('ouvertures.retour')}
        </Link>
      </section>
    );
  }

  const dossier = requete.data;
  const clos = estClos(dossier.etat);

  const ecrire: Ecriture = async (geste) => {
    setErreur(null);
    setAvertissement(null);
    try {
      const mis = await geste(dossier);
      queryClient.setQueryData(cleOuverture(id), mis);
      void queryClient.invalidateQueries({ queryKey: ['console', 'ouvertures'] });
      return true;
    } catch (echec) {
      await queryClient.invalidateQueries({ queryKey: cleOuverture(id) });
      if (estRevisionPerimee(echec)) setAvertissement(t('ouvertures.revisionPerimee'));
      else setErreur(messageErreurOuverture(t, echec));
      return false;
    }
  };

  const rang = ETATS_PARCOURS.findIndex((etat) => etat === dossier.etat);

  return (
    <section>
      <p>
        <Link to="/ouvertures" className="lien-cible">
          {t('ouvertures.retour')}
        </Link>
      </p>

      <div className={pageStyles.entete}>
        <div className={styles.enteteFiche}>
          <h1>
            {dossier.identite.nomCommercial}
            {dossier.identite.estTest ? (
              <>
                {' '}
                <BadgeTest />
              </>
            ) : null}
          </h1>
          <div className={styles.metaFiche}>
            <span className={styles.numero}>{dossier.numero}</span>
            <BadgeEtatOuverture etat={dossier.etat} />
            <span>
              {t('ouvertures.fiche.responsable')} :{' '}
              {dossier.responsable?.email ?? t('ouvertures.sansResponsable')}
            </span>
            <span>{t('ouvertures.fiche.creeLe', { date: formaterJour(dossier.creeLe) })}</span>
          </div>
        </div>
        {!clos && peut('console:manage-onboarding') && dossier.galleryId === null ? (
          <Link to={`/ouvertures/${dossier.id}/assistant`} className="lien-cible">
            {t('ouvertures.fiche.reprendreAssistant')}
          </Link>
        ) : null}
      </div>

      {dossier.etat === 'abandonne' ? (
        <p className={pageStyles.banniere}>{t('ouvertures.fiche.abandonne')}</p>
      ) : (
        <ol className={styles.frise} aria-label={t('ouvertures.fiche.frise')}>
          {ETATS_PARCOURS.map((etat, position) => (
            <li
              key={etat}
              className={`${styles.friseEtape} ${position < rang ? styles.friseFait : ''} ${
                position === rang ? styles.friseCourant : ''
              }`}
              aria-current={position === rang ? 'step' : undefined}
            >
              {t(`ouvertures.etat.${etat}`)}
            </li>
          ))}
        </ol>
      )}

      {avertissement ? (
        <p role="alert" className={pageStyles.banniere}>
          {avertissement}
        </p>
      ) : null}
      {erreur ? (
        <p role="alert" className={`${pageStyles.banniere} ${pageStyles.bannierePeril}`}>
          {erreur}
        </p>
      ) : null}

      <Tabs
        value={params.onglet}
        onValueChange={(valeur) => navigate({ pathname: `/ouvertures/${id}/${valeur}`, search })}
        tabs={[
          {
            value: 'synthese',
            label: t('ouvertures.fiche.onglets.synthese'),
            content: (
              <Synthese
                dossier={dossier}
                ecrire={ecrire}
                resiliation={galerieAResilier}
                surResiliation={setGalerieAResilier}
              />
            ),
          },
          {
            value: 'offre',
            label: t('ouvertures.fiche.onglets.offre'),
            content: <OffreContrat dossier={dossier} />,
          },
          {
            value: 'equipe',
            label: t('ouvertures.fiche.onglets.equipe'),
            content: <EquipeDossier dossier={dossier} />,
          },
          {
            value: 'reprise',
            label: t('ouvertures.fiche.onglets.reprise'),
            content: <RepriseDossier dossier={dossier} />,
          },
          {
            value: 'recette',
            label: t('ouvertures.fiche.onglets.recette'),
            content: <RecetteBascule dossier={dossier} />,
          },
          {
            value: 'journal',
            label: t('ouvertures.fiche.onglets.journal'),
            content: <JournalDossier dossier={dossier} />,
          },
        ]}
      />
    </section>
  );
}
